/** Pedir reseña (oct-2026) — el barbero muestra un QR / copia o comparte su enlace reutilizable. */
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Share2, RefreshCw, Star } from 'lucide-react';
import { toast } from 'sonner';
import { reviewLinksApi } from '@/lib/growthApi';
import { tr } from '@/contexts/LanguageContext';

export const RequestReviewDialog = ({ open, onOpenChange }) => {
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError(null);
    reviewLinksApi.mine().then((r) => { if (!cancelled) setLink(r); })
      .catch((e) => { if (!cancelled) setError(e?.payload?.detail || e.message || tr("No pudimos crear tu enlace")); });
    return () => { cancelled = true; };
  }, [open]);

  const url = link ? `${window.location.origin}${link.path}` : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(tr("Enlace copiado")); } catch { toast.error(tr("No pudimos copiar")); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: tr("Déjame tu reseña"), text: tr("¿Cómo quedó tu corte? Déjame tu reseña en Ziyup:"), url }); } catch { /* cancelado */ }
    } else copy();
  };
  const rotate = async () => {
    if (!window.confirm(tr("El enlace y el QR anteriores dejarán de funcionar. ¿Generar uno nuevo?"))) return;
    setBusy(true);
    try { setLink(await reviewLinksApi.rotate()); toast.success(tr("Nuevo enlace generado")); }
    catch (e) { toast.error(e?.payload?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="request-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Star className="w-5 h-5 text-primary fill-primary" />{' '}{tr("Pedir reseña")}</DialogTitle>
          <DialogDescription>{tr("Tu cliente escanea el QR o abre el enlace y deja su reseña pública. Puede hacerlo con su cuenta o como invitado (una por persona y por dispositivo). Las reseñas de invitados salen marcadas como «Invitado».")}</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-destructive" data-testid="request-review-error">{error}</p>}
        {link && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl p-4 mx-auto w-fit" data-testid="request-review-qr">
              <QRCode value={url} size={196} />
            </div>
            <p className="text-[11px] text-muted-foreground text-center break-all" data-testid="request-review-url">{url}</p>
            <p className="text-[11px] text-muted-foreground text-center" data-testid="request-review-uses">Usado {link.uses || 0} {link.uses === 1 ? tr("vez") : 'veces'}</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full gap-1.5" onClick={copy} data-testid="request-review-copy"><Copy className="w-4 h-4" />{' '}{tr("Copiar")}</Button>
              <Button className="rounded-full gap-1.5" onClick={share} data-testid="request-review-share"><Share2 className="w-4 h-4" />{' '}{tr("Compartir")}</Button>
            </div>
            <button type="button" onClick={rotate} disabled={busy} className="w-full text-[11px] text-muted-foreground inline-flex items-center justify-center gap-1 hover:text-foreground" data-testid="request-review-rotate">
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />{' '}{tr("Generar un enlace nuevo (invalida el anterior)")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
